import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CapacityCheck from '@shopDetails/components/CapacityCheck';
import MenuShimmer from '@conversion/components/MenuShimmer';
import InfiniteScroll from '@components/InfiniteScroll';
import ShelvesLayout from '@conversion/containers/ShelvesLayout';
import Shimmer from '@shopDetails/components/Shimmer';
import SearchInput from '@components/SearchInput';
import MenuSearch from '@shopDetails/pages/MenuSearch/Loadable';
import SectionDetailsFromSection from '@shopDetails/pages/SectionDetailsFromSection/Loadable';
import { getSectionRoute } from '@services/routes.service';
import { getLastUrlParam } from '@utils/url';
import { isShopDetailsSectionRoute } from '@shopDetails/router/utils';
import useSearchSuggestions from '@components/Search/Hooks/UseSearchSuggestions';
import connect from './connect';

const ShopDetailsContainer = styled(InfiniteScroll).attrs(props => ({
  id: props?.id ?? 'main-content',
}))`
  flex-grow: 1;
`;

const SearchInputContainer = styled.div`
  margin: ${({ theme }) => theme.spacing(12, 6, 0, 6)};
`;

const MenuSearchContainer = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(24)};
`;

function DesktopShopDetailsShelves({
  menu,
  hasToValidateAge,
  onBeforeProductConfig,
  countryCode,
  showShimmer,
  isFetchingMenu,
  capacityCheckActive,
  shelvesLoadStatus,
  sectionLoadStatus,
  shop,
  fetchMenu,
  fetchSectionsList,
  isFetchingSectionList,
  openAgeValidation,
  pushPage,
  match,
  location,
  shopId,
  userId,
}) {
  const [showSearch, setShowSearch] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);
  const [showSectionSearch, setShowSectionSearch] = useState(null);

  useEffect(() => {
    if (isShopDetailsSectionRoute(location?.pathname)) {
      const id = getLastUrlParam(location?.pathname);
      setCurrentSection(id);
      setShowSectionSearch(true);
    } else {
      setCurrentSection(null);
      setShowSectionSearch(false);
    }
  }, [location]);

  const fetchMoreShops = () => {
    if (!isFetchingMenu) {
      fetchMenu({ shop, isScrolling: true });
    }
    if (!isFetchingSectionList && sectionLoadStatus?.hasMore) {
      fetchSectionsList();
    }
  };

  const onShelveExpand = section => {
    const { city, restaurant } = match.params;
    const url = getSectionRoute({ city, restaurant, sectionId: section.id });
    pushPage(url);
  };

  const onValidateAge = (validated, section) => {
    if (validated) {
      onShelveExpand(section);
    }
  };

  const onOpenAgeValidation = section => {
    const wrapperFunc = validated => {
      onValidateAge(validated, section);
    };

    openAgeValidation({ onValidate: wrapperFunc });
  };

  const getShelvesLayoutConfiguration = () => ({
    fetchMore: fetchMoreShops,
    hasMore: shelvesLoadStatus?.hasMore || false,
    loader: shelvesLoadStatus?.hasMore && <MenuShimmer key="0" />,
  });

  const scrollConfig = getShelvesLayoutConfiguration();
  const { data, add } = useSearchSuggestions(userId, shopId);
  const showMenuShelves = !showSearch && !showSectionSearch;

  return (
    <>
      <ShopDetailsContainer
        fetchMore={scrollConfig.fetchMore}
        hasMore={scrollConfig.hasMore}
        loader={scrollConfig.loader}
        enabled
        id="main-content"
      >
        {!showSearch && !showSectionSearch && (
          <SearchInputContainer id="desktop_shelves_input_container">
            <SearchInput
              onClick={() => setShowSearch(true)}
              placeholder="Buscar..."
              disabled
            />
          </SearchInputContainer>
        )}

        {Boolean(capacityCheckActive) && <CapacityCheck />}
        {showShimmer && <Shimmer />}

        {showMenuShelves && (
          <>
            <ShelvesLayout
              menu={menu}
              isFetchingMenu={isFetchingMenu}
              onBeforeProductConfig={onBeforeProductConfig}
              openAgeValidation={onOpenAgeValidation}
              hasToValidateAge={hasToValidateAge}
              countryCode={countryCode}
            />
          </>
        )}

        {showSearch && (
          <MenuSearchContainer>
            <MenuSearch
              match={match}
              withGoBackLabel
              suggestions={data}
              addSuggestion={add}
              onGoBack={() => setShowSearch(false)}
            />
          </MenuSearchContainer>
        )}

        {showSectionSearch && (
          <SectionDetailsFromSection sectionId={currentSection} />
        )}
      </ShopDetailsContainer>
    </>
  );
}

DesktopShopDetailsShelves.propTypes = {
  // data
  isFetchingSectionList: PropTypes.bool.isRequired,
  isFetchingMenu: PropTypes.bool.isRequired,
  capacityCheckActive: PropTypes.bool,
  shelvesLoadStatus: PropTypes.shape({
    count: PropTypes.number,
    hasMore: PropTypes.bool,
  }),
  sectionLoadStatus: PropTypes.shape({
    count: PropTypes.number,
    hasMore: PropTypes.bool,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      city: PropTypes.string,
      restaurant: PropTypes.string,
    }),
  }).isRequired,
  pushPage: PropTypes.func.isRequired,
  shopId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.number,

  // WithDesktopShopDetails
  shop: PropTypes.object,
  menu: PropTypes.object,
  hasToValidateAge: PropTypes.bool.isRequired,
  countryCode: PropTypes.string.isRequired,
  showShimmer: PropTypes.bool.isRequired,
  onBeforeProductConfig: PropTypes.func.isRequired,
  openAgeValidation: PropTypes.func.isRequired,
  location: PropTypes.object,

  // callbacks
  fetchMenu: PropTypes.func.isRequired,
  fetchSectionsList: PropTypes.func.isRequired,
};

export { DesktopShopDetailsShelves as ShopDetailsTests };
export default connect(DesktopShopDetailsShelves);
